
document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

/**
 * A11y Helpers
 * -----------------------------------------------------------------------------
 * A collection of useful functions that help make your theme more accessible
 */

/**
 * Moves focus to an HTML element
 * eg for In-page links, after scroll, focus shifts to content area so that
 * next `tab` is where user expects. Used in bindInPageLinks()
 * eg move focus to a modal that is opened. Used in trapFocus()
 *
 * @param {Element} container - Container DOM element to trap focus inside of
 * @param {Object} options - Settings unique to your theme
 * @param {string} options.className - Class name to apply to element on focus.
 */
function forceFocus(element, options) {
  if (options instanceof Element) {
    window.forceFocus(element, {});
    return;
  }
  if (options && typeof options.className === 'string' && /\s/.test(options.className)) {
    const first = options.className.trim().split(/\s+/)[0];
    window.forceFocus(element, Object.assign({}, options, first ? { className: first } : {}));
    return;
  }
  window.forceFocus(element, options || {});
}

/**
 * Theme `trapFocus(container, options)` expects a plain options object with
 * `elementToFocus` (not Dawn's `trapFocus(container, element)`). If a DOM
 * node is passed as `options`, theme forwards it to `forceFocus` and treats
 * `options.className` as the node's full class string → InvalidCharacterError.
 */
function trapFocus(container, options) {
  const safe = {};
  if (options == null || typeof options !== 'object') {
    window.trapFocus(container, safe);
    return;
  }
  if (typeof Element !== 'undefined' && options instanceof Element) {
    safe.elementToFocus = options;
  } else {
    if (typeof Element !== 'undefined' && options.elementToFocus instanceof Element) {
      safe.elementToFocus = options.elementToFocus;
    }
    if (typeof options.className === 'string' && options.className.trim()) {
      const firstToken = options.className.trim().split(/\s+/)[0];
      if (firstToken) safe.className = firstToken;
    }
  }
  window.trapFocus(container, safe);
}

/**
 * Removes the trap of focus from the page
 * @param {Element} [elementToFocus] - optional element to restore focus (theme removeTrapFocus has no args)
 */
function removeTrapFocus(elementToFocus) {
  window.removeTrapFocus();
  if (elementToFocus && typeof elementToFocus.focus === 'function') {
    elementToFocus.focus();
  }
}

/** Tab stops inside drawer; includes `summary` (theme.js focusable() does not). */
const DRAWER_TABBABLE_SELECTOR =
  'summary, a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function getDrawerTabbables(root) {
  if (!root || typeof root.querySelectorAll !== 'function') return [];
  return Array.from(root.querySelectorAll(DRAWER_TABBABLE_SELECTOR)).filter((el) => {
    if (el.hasAttribute('disabled')) return false;
    const style = window.getComputedStyle(el);
    if (style.visibility === 'hidden' || style.display === 'none') return false;
    return Boolean(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
  });
}

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keydown', this.onDrawerTabCycle, true);
    this._activeScrollLockTarget = null;
    this.addEventListener('theme:drawer:open', () => {
      // Drawer visibility is controlled by theme.js. Refresh target after it opens.
      setTimeout(() => this.refreshScrollLockTarget(), 10);
    });
    this.addEventListener('theme:drawer:close', () => {
      this._activeScrollLockTarget = null;
    });
    this.bindEvents();
  }

  bindEvents() {
    super.bindEvents();
    // this.querySelector('[data-drawer-toggle]').addEventListener('click', this.onDrawerCloseClick.bind(this));
  }

  // onDrawerCloseClick(event) {
  //   this.closeMenuDrawer(event);
  // }

  /** Drawer is open (theme adds `drawer--visible`) or main menu details is open. */
  drawerTrapActive() {
    if (this.classList.contains('drawer--visible')) return true;
    if (this.mainDetailsToggle && this.mainDetailsToggle.hasAttribute('open')) return true;
    return false;
  }

  /**
   * While a submenu `details` is open, Tab should stay inside that branch — not jump to
   * `.menu-drawer__utility-links` (social, localization, etc.). Tree order: last `details[open]`
   * under nav is the innermost expanded submenu.
   */
  getTabCycleRoot() {
    const navRoot = this.querySelector('nav.menu-drawer__navigation') ||
      this.querySelector('.menu-drawer__navigation');
    if (!navRoot) return this;
    const openStack = navRoot.querySelectorAll('details[open]');
    if (!openStack.length) return this;
    return openStack[openStack.length - 1];
  }

  /**
   * Theme trapFocus uses a focusable list that omits `summary`, so Tab can leave the drawer.
   * While the drawer is open, keep Tab cycling inside `getTabCycleRoot()` (whole drawer, or
   * innermost open submenu only).
   */
  onDrawerTabCycle(event) {
    if (event.key !== 'Tab' && event.keyCode !== 9) return;
    if (!this.drawerTrapActive()) return;
    if (!this.contains(document.activeElement)) return;

    const cycleRoot = this.getTabCycleRoot();
    if (!cycleRoot.contains(document.activeElement)) return;

    const tabbables = getDrawerTabbables(cycleRoot);
    if (!tabbables.length) return;

    const active = document.activeElement;
    const index = tabbables.indexOf(active);
    if (index === -1) return;

    if (!event.shiftKey && index === tabbables.length - 1) {
      event.preventDefault();
      tabbables[0].focus();
      return;
    }
    if (event.shiftKey && index === 0) {
      event.preventDefault();
      tabbables[tabbables.length - 1].focus();
    }
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button):not([data-drawer-toggle])'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      const panel = summaryElement.nextElementSibling;
      const focusTarget =
        detailsElement.querySelector('button') ||
        panel?.querySelector('button, a[href]') ||
        summaryElement;
      trapFocus(detailsElement, { elementToFocus: focusTarget });
      panel?.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);

        // iOS: switch body-scroll-lock target to active submenu scroller.
        this.refreshScrollLockTarget();
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this, { elementToFocus: summaryElement });
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
    setTimeout(() => this.refreshScrollLockTarget(), 0);
  }

  getPreferredScrollLockTarget() {
    // Only treat currently active/opening submenu panels as scroll targets.
    // During close animation, `open` may still exist for ~400ms and would otherwise
    // keep body-scroll-lock bound to a submenu that is no longer interactive.
    const openStack = Array.from(this.querySelectorAll('details[open].menu-opening'));
    for (let i = openStack.length - 1; i >= 0; i -= 1) {
      const submenuScroller = openStack[i].querySelector('.menu-drawer__inner-submenu');
      if (submenuScroller) return submenuScroller;
    }

    return (
      this.querySelector('.menu-drawer__navigation-container') ||
      this.querySelector('[data-drawer-scrolls]') ||
      this
    );
  }

  refreshScrollLockTarget() {
    if (!this.classList.contains('drawer--visible')) return;
    const target = this.getPreferredScrollLockTarget();
    if (!target || this._activeScrollLockTarget === target) return;

    this._activeScrollLockTarget = target;
    document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('theme:scroll:lock', { bubbles: true, detail: target }));
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        const parentOpen = detailsElement.closest('details[open]');
        if (parentOpen) {
          trapFocus(parentOpen, {
            elementToFocus: detailsElement.querySelector('summary'),
          });
        } else {
          trapFocus(this, {
            elementToFocus: detailsElement.querySelector('summary'),
          });
        }
        this.refreshScrollLockTarget();
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('[data-header-wrapper]') || document.querySelector('.section-header');
    // this.borderOffset =
    //   this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    // document.documentElement.style.setProperty(
    //   '--header-bottom-position',
    //   `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    // );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    this.onResize();
    window.addEventListener('resize', this.onResize);
    window.visualViewport?.addEventListener('resize', this.onResize);
    window.visualViewport?.addEventListener('scroll', this.onResize);
    trapFocus(this, { elementToFocus: summaryElement });
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
    setTimeout(() => this.refreshScrollLockTarget(), 10);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
    window.visualViewport?.removeEventListener('resize', this.onResize);
    window.visualViewport?.removeEventListener('scroll', this.onResize);
    this._activeScrollLockTarget = null;
  }

  onResize = () => {
    // this.header &&
    //   document.documentElement.style.setProperty(
    //     '--header-bottom-position',
    //     `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    //   );
    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    document.documentElement.style.setProperty('--viewport-height', `${viewportHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);